import { ColumnsType } from 'antd/es/table';
import { Status } from '../../../components';
import { convertNameToPath, Statuses } from '../../../helper';
import { AnnualReviewUsers } from '../../../types/AnnualReview';
import { Button, Tooltip } from 'antd';
import { FieldTimeOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAtom } from 'jotai';
import { userAtom } from '../../../store/atom';
import { Paths } from '../../../routes';
import AuditTrailAnnual from '../../../components/audit-trail-annual';
import { formattedDateTime } from '../../../helper/functions';
import { MenteeName } from '../employee-dashboard/columns';

export const AnnualCycleUsersColumns = (cycleName: string): ColumnsType => {
  const location = useLocation();
  const currentUrl = location.pathname;
  const { selectedId } = location.state;
  return [
    {
      title: 'Mentee',
      key: 'name',
      render: (_, record: AnnualReviewUsers) => (
        <MenteeName
          //@ts-ignore
          data={{
            name: record.name,
            id: record.userId,
            designation: record.designation,
            tier: record.tier.toString(),
            mentor: record.mentor,
          }}
          path={`${currentUrl}/${convertNameToPath(record.name)}`}
          pathSate={{
            userId: record.userId,
            from: 'hr',
            selectedCycleId: selectedId,
          }}
        />
      ),
      sorter: (a: AnnualReviewUsers, b: AnnualReviewUsers) => {
        return a.name?.trim().localeCompare(b.name?.trim());
      },
    },
    {
      title: 'Designation',
      dataIndex: 'designation',
      key: 'designation',
      render: (designation) => designation,
      sorter: (a: AnnualReviewUsers, b: AnnualReviewUsers) => {
        return a.designation?.trim().localeCompare(b.designation?.trim());
      },
    },
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      render: (tier) => (Number(tier) > 6 ? 'N/A' : tier),
      sorter: (a: AnnualReviewUsers, b: AnnualReviewUsers) => {
        return a.tier?.toString().localeCompare(b.tier?.toString());
      },
    },
    {
      title: 'Mentor Name',
      dataIndex: 'mentor',
      key: 'mentor',
      render: (mentor) => mentor.name,
      sorter: (a: AnnualReviewUsers, b: AnnualReviewUsers) => {
        return a.mentor?.name.trim().localeCompare(b.mentor?.name?.trim());
      },
    },
    {
      title: 'Mentor Feedback Status',
      key: 'mentorFeedbackStatus',
      sorter: (a: AnnualReviewUsers, b: AnnualReviewUsers) =>
        a.mentorFeedbackStatus
          ?.trim()
          .localeCompare(b.mentorFeedbackStatus?.trim()),
      render: (data: AnnualReviewUsers) => {
        if (data.mentorFeedbackStatus === Statuses.Completed) {
          return (
            <>
              <Status status={data.mentorFeedbackStatus} />
              {data.mentorSubmissionDate && (
                <div className="w-fit">
                  <p className="text-gray-500 bg-gray-100 rounded-lg text-sm text-center py-1 px-3">
                    On {formattedDateTime(data.mentorSubmissionDate)}
                  </p>
                </div>
              )}
            </>
          );
        }
        return <Status status={data.mentorFeedbackStatus} />;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (data: AnnualReviewUsers) => (
        <>
          <div className="flex items-center space-x-2">
            <AnnualCycle
              data={{
                id: data.userId,
                name: data.name,
                tier: data.tier,
                designation: data.designation,
                mentor: {
                  ...data.mentor,
                  tier: extractNumber(data.mentor.tier as string),
                },
              }}
              cycleName={cycleName}
              feedbackStatus={data.mentorFeedbackStatus}
            />

            <ViewAudit
              id={data.userReviewId as string}
              feedbackStatus={data.mentorFeedbackStatus}
            />
          </div>
        </>
      ),
    },
  ];
};

const ViewAudit = ({
  id,
  feedbackStatus,
}: {
  id: string;
  feedbackStatus: string;
}) => {
  const [open, setOpen] = useState(false);
  const hanldeClick = () => setOpen(true);
  return (
    <Tooltip title="View History" placement="top">
      <Button
        icon={<FieldTimeOutlined />}
        onClick={hanldeClick}
        disabled={feedbackStatus !== Statuses.Completed ? true : false}
      />
      <AuditTrailAnnual setOpen={setOpen} open={open} id={id} />
    </Tooltip>
  );
};

const AnnualCycle = ({
  data,
  cycleName,
  feedbackStatus,
}: {
  data: any;
  cycleName: string;
  feedbackStatus: string;
}) => {
  const [userState, setUserState] = useAtom(userAtom);
  const navigate = useNavigate();

  const handleClick = () => {
    setUserState({
      ...userState,
      selectedMentee: data,
    });

    navigate(
      `${Paths.HRAllAnnualReviews}/${convertNameToPath(cycleName)}/${convertNameToPath(data?.name || '')}/annual-feedback`,
      { state: { from: 'hr' } }
    );
  };

  return (
    <Button
      type="text"
      style={{
        backgroundColor: `${feedbackStatus !== Statuses.Completed ? '#e9ecef' : '#E7F5FF'}`,
        border: `1px solid ${feedbackStatus !== Statuses.Completed ? '#adb5bd' : '#228BE6'} `,
        color: `${feedbackStatus !== Statuses.Completed ? '#adb5bd' : '#228BE6'}`,
      }}
      onClick={handleClick}
      disabled={feedbackStatus !== Statuses.Completed}
    >
      View Current Annual Review
    </Button>
  );
};

const extractNumber = (label: string): string | null => {
  if (typeof label !== 'string') {
    return null;
  }
  const match = label.match(/\d+/);
  return match ? match[0] : null;
};
